import React, { useEffect, useState } from 'react';
import type { FC } from 'react';
import { Link, useLocation } from 'react-router-dom';
import type { IconBaseProps, IconType } from 'react-icons';
import {
  FiChevronDown,
  FiCpu,
  FiGrid,
  FiHardDrive,
  FiHeadphones,
  FiMonitor,
  FiTag,
  FiX,
} from 'react-icons/fi';
import { toCategoryHref } from '../../../utils/constants/navigation.constants';
import { useWooCategoryNavigation } from '../../../hooks/useWooCategoryNavigation';

const renderIcon = (Icon: IconType, props?: IconBaseProps) =>
  React.createElement(Icon as unknown as React.FC<IconBaseProps>, props);

const categoryIcons: Record<string, IconType> = {
  Laptop: FiMonitor,
  'Desktop PC': FiCpu,
  Components: FiHardDrive,
  Accessories: FiHeadphones,
  Printers: FiGrid,
  Projectors: FiMonitor,
  'POS System': FiGrid,
  Software: FiTag,
  'Used Items': FiTag,
  OFFERS: FiTag,
};

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

const MobileMenu: FC<MobileMenuProps> = ({ isOpen, onClose }) => {
  const { pathname } = useLocation();
  const [openCategories, setOpenCategories] = useState<string[]>([]);
  const menuItems = useWooCategoryNavigation();

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1280) onClose();
    };

    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      setOpenCategories([]);
    }

    window.addEventListener('resize', handleResize);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen, onClose]);

  const toggleItem = (label: string) => {
    setOpenCategories((prev) =>
      prev.includes(label) ? prev.filter((category) => category !== label) : [...prev, label]
    );
  };

  const isActivePath = (href: string): boolean =>
    pathname === href || (href !== '/' && pathname.startsWith(`${href}/`));

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[120] overflow-hidden xl:hidden" role="dialog" aria-modal="true" aria-label="Mobile navigation menu">
      <button className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} aria-label="Close mobile menu overlay" />

      <aside className="absolute right-0 top-0 flex h-full w-[min(100vw,26rem)] max-w-full flex-col overflow-hidden border-l border-white/10 bg-slate-950 text-white shadow-2xl shadow-black/60">
        <div className="flex min-w-0 items-center justify-between gap-4 border-b border-white/10 px-4 py-4">
          <div className="min-w-0">
            <p className="break-words text-[clamp(1rem,4vw,1.125rem)] font-black tracking-wide">Categories</p>
            <p className="text-[clamp(0.75rem,3vw,0.8125rem)] text-slate-400">Shop by product type</p>
          </div>
          <button
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/20 bg-white/5 text-slate-100 transition hover:border-indigo-400 hover:bg-indigo-500/20"
            onClick={onClose}
            aria-label="Close mobile menu"
          >
            {renderIcon(FiX, { size: 20 })}
          </button>
        </div>

        <div className="min-w-0 flex-1 overflow-y-auto px-3 py-3 sm:px-4">
          <nav className="flex min-w-0 flex-col border-t border-slate-800/50">
            {menuItems.map((item) => {
              const hasChildren = Boolean(item.children?.length);
              const expanded = openCategories.includes(item.label);
              const isParentActive = isActivePath(item.basePath);
              const Icon = categoryIcons[item.label] || FiGrid;

              if (!hasChildren) {
                return (
                  <Link
                    key={item.label}
                    to={item.basePath}
                    target="_self"
                    onClick={onClose}
                    className={`relative flex min-w-0 items-center justify-between gap-3 border-b border-slate-800/50 px-3 py-3.5 text-[clamp(0.875rem,3.4vw,1rem)] font-semibold transition hover:bg-slate-900/70 hover:text-indigo-300 ${
                      isParentActive ? 'bg-slate-900/80 text-indigo-400' : 'text-slate-100'
                    }`}
                  >
                    <span className="flex min-w-0 items-center gap-3">
                      {renderIcon(Icon, { size: 18, className: 'shrink-0 text-slate-400' })}
                      <span className="min-w-0 break-words">{item.label}</span>
                    </span>
                  </Link>
                );
              }

              return (
                <div key={item.label} className="min-w-0 border-b border-slate-800/50 py-1">
                  <div className={`relative flex min-w-0 items-stretch overflow-hidden rounded-xl transition hover:bg-gradient-to-r hover:from-indigo-500 hover:to-purple-600 hover:text-white ${
                      isParentActive ? 'bg-slate-900/80 text-indigo-400' : 'text-slate-100'
                    }`}>
                    <Link
                      to={item.basePath}
                      target="_self"
                      onClick={onClose}
                      className="flex min-w-0 flex-1 items-center gap-3 px-3 py-3.5 text-left text-[clamp(0.875rem,3.4vw,1rem)] font-semibold"
                    >
                      {renderIcon(Icon, { size: 18, className: 'shrink-0 text-slate-400' })}
                      <span className="min-w-0 break-words">{item.label}</span>
                    </Link>
                    <button
                      type="button"
                      className="flex w-12 shrink-0 items-center justify-center border-l border-white/10 hover:bg-white/15"
                      onClick={() => toggleItem(item.label)}
                      aria-label={`${expanded ? 'Collapse' : 'Expand'} ${item.label} categories`}
                      aria-expanded={expanded}
                      aria-controls={`mobile-category-panel-${item.label.replace(/\s+/g, '-').toLowerCase()}`}
                    >
                      <span className={`shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`}>
                        {renderIcon(FiChevronDown, { size: 16 })}
                      </span>
                    </button>
                  </div>

                  {expanded ? (
                    <div
                      id={`mobile-category-panel-${item.label.replace(/\s+/g, '-').toLowerCase()}`}
                      className="min-w-0 space-y-1 border-t border-slate-800/50 bg-slate-950/50 px-2 pb-2 pt-1"
                    >
                      {item.children?.map((child) => {
                        const childHref = child.href || toCategoryHref(item.basePath, child.slug);
                        const isChildActive = pathname === childHref;

                        return (
                          <Link
                            key={child.slug}
                            to={childHref}
                            target="_self"
                            onClick={onClose}
                            className={`block min-w-0 break-words rounded-lg px-3 py-2 text-[clamp(0.8125rem,3.2vw,0.875rem)] font-medium leading-5 transition hover:bg-indigo-500/10 hover:text-indigo-200 ${
                              isChildActive ? 'text-indigo-400' : 'text-slate-200'
                            }`}
                          >
                            {child.label}
                          </Link>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </nav>
        </div>
      </aside>
    </div>
  );
};

export default MobileMenu;
